'use client';

import { useCallback, useMemo, useRef, useState } from 'react';

const DEFAULT_LENGTH = 5;

interface CharacterBlockInputProps {
  /** Current answer value — the single source of truth */
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
}

/**
 * Per-character block input (OTP-style) used by the captcha challenges.
 * Handles auto-advance, backspace-to-previous, arrow navigation and paste
 * distribution across the blocks.
 */
export function CharacterBlockInput({
  value,
  onChange,
  length = DEFAULT_LENGTH,
  disabled = false,
  autoFocus = false,
}: CharacterBlockInputProps) {
  const blockRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [activeBlock, setActiveBlock] = useState(-1);

  // Derive the block values from the canonical answer string
  const blockValues = useMemo(() => {
    const chars = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, length);
    const values: string[] = [];
    for (let i = 0; i < length; i++) {
      values.push(chars[i] || '');
    }
    return values;
  }, [value, length]);

  const updateFromBlocks = useCallback(
    (newBlocks: string[]) => {
      const joined = newBlocks.join('').slice(0, length);
      onChange(joined);
    },
    [onChange, length],
  );

  const handleBlockChange = useCallback(
    (blockIdx: number, rawValue: string) => {
      const current = blockValues[blockIdx] || '';
      const newBlocks = [...blockValues];

      // Strip non-alphanumeric, uppercase
      const cleaned = rawValue.toUpperCase().replace(/[^A-Z0-9]/g, '');

      // Deletion: input cleared or got shorter
      if (cleaned === '' || cleaned.length < current.length) {
        newBlocks[blockIdx] = cleaned.slice(0, 1);
        updateFromBlocks(newBlocks);
        if (newBlocks[blockIdx].length === 0 && blockIdx > 0) {
          window.setTimeout(() => {
            blockRefs.current[blockIdx - 1]?.focus();
            setActiveBlock(blockIdx - 1);
          }, 0);
        }
        return;
      }

      // Single keypress: rawValue is current + exactly one new char
      if (cleaned.length === current.length + 1 && cleaned.startsWith(current)) {
        const newChar = cleaned[current.length];
        newBlocks[blockIdx] = newChar;
        updateFromBlocks(newBlocks);

        // Auto-advance to next block when filled
        if (blockIdx < length - 1) {
          window.setTimeout(() => {
            blockRefs.current[blockIdx + 1]?.focus();
            setActiveBlock(blockIdx + 1);
          }, 0);
        }
        return;
      }

      // Paste or multi-char insertion: distribute chars across blocks
      const allChars = cleaned.slice(0, length);
      let cursor = blockIdx;
      let charIdx = 0;
      while (charIdx < allChars.length && cursor < length) {
        newBlocks[cursor] = allChars[charIdx];
        charIdx++;
        cursor++;
      }
      updateFromBlocks(newBlocks);

      const focusIdx = Math.min(cursor, length - 1);
      window.setTimeout(() => {
        const el = blockRefs.current[focusIdx];
        if (el) {
          el.focus();
          const len = el.value.length;
          el.setSelectionRange(len, len);
        }
        setActiveBlock(focusIdx);
      }, 0);
    },
    [blockValues, updateFromBlocks, length],
  );

  const handleBlockKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, blockIdx: number) => {
      if (e.key === 'Backspace') {
        const current = blockValues[blockIdx] || '';
        if (current.length === 0 && blockIdx > 0) {
          e.preventDefault();
          const newBlocks = [...blockValues];
          newBlocks[blockIdx - 1] = '';
          updateFromBlocks(newBlocks);
          window.setTimeout(() => {
            blockRefs.current[blockIdx - 1]?.focus();
            setActiveBlock(blockIdx - 1);
          }, 0);
        }
      } else if (e.key === 'ArrowLeft' && blockIdx > 0) {
        e.preventDefault();
        blockRefs.current[blockIdx - 1]?.focus();
        setActiveBlock(blockIdx - 1);
      } else if (e.key === 'ArrowRight' && blockIdx < length - 1) {
        e.preventDefault();
        blockRefs.current[blockIdx + 1]?.focus();
        setActiveBlock(blockIdx + 1);
      }
    },
    [blockValues, updateFromBlocks, length],
  );

  const handleBlockPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>, blockIdx: number) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, length);
      if (!pasted) return;
      const newBlocks = [...blockValues];
      let charIdx = 0;
      let cursor = blockIdx;
      while (charIdx < pasted.length && cursor < length) {
        newBlocks[cursor] = pasted[charIdx];
        charIdx++;
        cursor++;
      }
      updateFromBlocks(newBlocks);
      const focusIdx = Math.min(cursor, length - 1);
      window.setTimeout(() => {
        const el = blockRefs.current[focusIdx];
        if (el) {
          el.focus();
          const len = el.value.length;
          el.setSelectionRange(len, len);
        }
        setActiveBlock(focusIdx);
      }, 0);
    },
    [blockValues, updateFromBlocks, length],
  );

  return (
    <div className="flex space-x-1.5 sm:space-x-2 justify-center">
      {blockValues.map((char, blockIdx) => (
        <input
          key={blockIdx}
          id={`captcha-block-${blockIdx}`}
          ref={(el) => {
            blockRefs.current[blockIdx] = el;
          }}
          type="text"
          aria-label={`Character ${blockIdx + 1}`}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={1}
          value={char}
          onChange={(e) => handleBlockChange(blockIdx, e.target.value)}
          onKeyDown={(e) => handleBlockKeyDown(e, blockIdx)}
          onPaste={(e) => handleBlockPaste(e, blockIdx)}
          onFocus={() => setActiveBlock(blockIdx)}
          onBlur={() => setActiveBlock(-1)}
          disabled={disabled}
          autoFocus={autoFocus && blockIdx === 0}
          className="w-10 h-10 sm:w-12 sm:h-12 text-center text-lg sm:text-xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all font-mono uppercase"
          style={{
            borderColor: char ? '#f59e0b' : '#e5e7eb',
            backgroundColor: char ? '#fffbeb' : 'white',
            color: char ? '#92400e' : '#1f2937',
          }}
        />
      ))}
    </div>
  );
}
